/**
 * Las notificaciones del negocio, en vivo.
 *
 * POR QUÉ EXISTE. El founder: «cuando Smarty apruebe la recarga, en tiempo real debe aparecerle el
 * saldo al dropshipper, o una alerta bonita en la pantalla que diga: recarga tal… aprobada». Y del
 * otro lado: «en Smarty HQ también debe llegar en tiempo real una notificación».
 *
 * 🪤 EL EVENTO YA SE ESCRIBÍA Y NADIE SE ENTERABA. La tabla `notifications` existe y tiene su
 *    bandeja en el panel, pero no estaba publicada para realtime: el navegador la veía sólo al
 *    recargar. «En tiempo real» no era una función que faltaba — era una tabla sin publicar.
 *
 * ⭐ ESCUCHA LA TABLA CANÓNICA, no `drop_pagos`. Cualquier aviso que el sistema decida emitir
 *    —una recarga, una venta, una integración caída— aparece acá sin tocar este archivo. Atarlo a
 *    la tabla de pagos habría resuelto la recarga y dejado el resto para la próxima vez.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@smarty/shared';
import { supabase } from '@/lib/supabase';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { CLAVES_DROP, type ClaveDrop } from '@/lib/dropshipping/clavesDeCache';
import { CLAVES_DE_LA_PLATA } from '@/lib/dropshipping/laPlataCambio';
import { useAbrirAviso } from '@/hooks/useAbrirAviso';
import { aDondeLlevaElAviso } from '@/lib/avisos/aDondeLlevaElAviso';

interface FilaNotificacion {
  id: string;
  type: string | null;
  title: string | null;
  body: string | null;
  tenant_id: string | null;
  url: string | null;
  data: Record<string, unknown> | null;
}

/**
 * Qué se recarga cuando llega cada aviso. Un aviso que no refresca nada deja al usuario mirando
 * un número viejo justo después de decirle que cambió.
 */
/*
 * 🚨 ACÁ DECÍA `drop-saldo` (una clave que ya no existe: es `drop-mi-saldo`) Y NO NOMBRABA
 *    `drop-cobros`, que es la lista que mira HQ. El founder: «no llegó la recarga en tiempo real,
 *    tuve que actualizar». El aviso llegaba y refrescaba la clave equivocada (21-sep-2026).
 *    La regla la aplica el TIPO: `ClaveDrop` sólo admite claves del registro `CLAVES_DROP`, que es
 *    el mismo que usan las pantallas. Una clave inventada no compila.
 */
export const REFRESCAR_POR_TIPO: Record<string, ClaveDrop[]> = {
  // Un aviso de plata refresca la familia de la plata entera (auditoría 26-sep): la liquidación y los
  // saldos por cuenta también la muestran.
  'drop.recarga_aprobada': [...CLAVES_DE_LA_PLATA],
  'drop.recarga_declarada': [...CLAVES_DE_LA_PLATA],
  // El pago de un pedido: mismo par, y del lado del revendedor cambia el pedido y la venta.
  'drop.pago_declarado': [...CLAVES_DE_LA_PLATA],
  'drop.pago_aprobado': [...CLAVES_DE_LA_PLATA],
  // Smarty publicó una novedad: la tarjeta del Inicio y el muro la muestran sin recargar, y si es
  // importante, el aviso sale en ese momento.
  'smarty.novedad': [CLAVES_DROP.novedades, CLAVES_DROP.avisoDeNovedad],
  // 🪤 `drop-mi-progreso` vivía acá y ninguna pantalla la usaba desde que se retiró el checklist del
  //    Inicio del Drop. El candado que debía cazarlo tenía la lista escrita a mano — y la incluía.
  //    Ahora el tipo `ClaveDrop` no deja escribir una clave que no exista.
};

export function useNotificacionesEnVivo() {
  const { tenantId } = useAuth();
  const toast = useAdminToast();
  const queryClient = useQueryClient();
  const abrirAviso = useAbrirAviso();

  /*
   * 🪤 Las funciones van por `ref`. `useAdminToast` y el cliente de queries cambian de identidad
   *    entre renders, y si entraran como dependencias el canal se desuscribiría y volvería a
   *    suscribirse todo el tiempo — perdiendo justo los avisos que llegan en el medio.
   */
  const refs = useRef({ toast, queryClient, abrirAviso });
  refs.current = { toast, queryClient, abrirAviso };

  useEffect(() => {
    if (!tenantId) return;

    const canal = supabase
      .channel(`avisos-${tenantId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          // 🔒 El filtro es comodidad, no seguridad: quien aísla de verdad es la RLS de la tabla,
          //    que realtime respeta. Sin ese candado esto sería un canal a las filas de todos.
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => {
          const fila = payload.new as FilaNotificacion;
          if (!fila?.title) return;

          const { toast: avisar, queryClient: qc, abrirAviso: abrir } = refs.current;
          // «Ver» lleva a lo mismo que el clic en la campana: un aviso de «llegó a la agencia» sin
          // camino al pedido obliga a ir a buscarlo.
          const aviso = { type: fila.type ?? '', url: fila.url ?? null, data: fila.data ?? null };
          const ver = aDondeLlevaElAviso(aviso) ? { content: 'Ver', onAction: () => { abrir(aviso); } } : undefined;
          // El tono lo decide el evento: aprobar es una buena noticia, revisar es trabajo pendiente.
          if (fila.type === 'drop.recarga_aprobada' || fila.type === 'drop.pago_aprobado') avisar.success(fila.title, fila.body ?? undefined, ver);
          else avisar.info(fila.title, fila.body ?? undefined, ver);

          // La página de todas las notificaciones, si está abierta, suma la nueva arriba.
          void qc.invalidateQueries({ queryKey: ['notificaciones-todas'] });
          for (const clave of REFRESCAR_POR_TIPO[fila.type ?? ''] ?? []) {
            void qc.invalidateQueries({ queryKey: [clave] });
          }
        },
      )
      .subscribe();

    return () => { void supabase.removeChannel(canal); };
  }, [tenantId]);
}
